import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import ManageMaterialsDialog from "@/components/ManageMaterialsDialog";
import ManageColoursDialog from "@/components/ManageColoursDialog";
import { Sparkles, PaintBucket, Ruler, ChevronRight, ArrowRight, Loader2, Package, Hammer, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

/**
 * Manager-only Dashboard widget that gently nudges the manager to enrich
 * admin-list entries that were quick-added mid-form (or are stale legacy
 * records). Silent when everything is tidy — no zero-state noise.
 *
 * Buckets returned by GET /api/admin-lists/nudges:
 *   - materials_missing_palette : no colours[] set yet
 *   - materials_missing_thickness : no thicknesses[] set yet
 *   - colours_missing_swatch : neither swatch (hex) nor swatch_image
 */
const NudgeSection = ({ icon: Icon, title, rows, onFix, onMove, testid, movable }) => {
  const [movingName, setMovingName] = useState(null);
  if (!rows.length) return null;
  return (
    <div className="border-2 border-black rounded-xl bg-white p-3" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon size={16} strokeWidth={2.5} className="text-[#5B3A99]" />
          <div className="font-display text-sm leading-tight">
            {title}
            <span className="ml-1.5 label-eyebrow px-1.5 py-0.5 rounded border-2 border-black bg-[#F1E7FA] text-[#5B3A99]">
              {rows.length}
            </span>
          </div>
        </div>
        <button
          data-testid={`${testid}-fix`}
          onClick={onFix}
          className="nb-btn bg-white px-2.5 py-1 text-xs flex items-center gap-1"
        >
          Tidy up <ChevronRight size={12} strokeWidth={2.5} />
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {rows.slice(0, 8).map((r) => (
          <span
            key={r.id}
            data-testid={`${testid}-row-${r.name}`}
            className="text-xs px-2 py-0.5 rounded border-2 border-black bg-[#F4F8FB] flex items-center gap-1"
          >
            {r.name}
            {movable && (
              <button
                data-testid={`${testid}-move-${r.name}`}
                onClick={async () => {
                  if (movingName) return;
                  setMovingName(r.name);
                  try {
                    await onMove(r.name);
                  } finally {
                    setMovingName(null);
                  }
                }}
                disabled={movingName === r.name}
                title="Move to Category (not a real material)"
                className="text-[10px] px-1 py-0.5 rounded border border-[#5B3A99] bg-white text-[#5B3A99] hover:bg-[#F1E7FA] disabled:opacity-50 flex items-center gap-0.5"
              >
                {movingName === r.name ? (
                  <Loader2 size={10} className="animate-spin" />
                ) : (
                  <ArrowRight size={10} strokeWidth={2.5} />
                )}
                to Category
              </button>
            )}
          </span>
        ))}
        {rows.length > 8 && (
          <span className="text-xs text-[#607087] italic self-center">
            +{rows.length - 8} more
          </span>
        )}
      </div>
    </div>
  );
};

const AdminListNudgesWidget = () => {
  const { isManager } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [materialsOpen, setMaterialsOpen] = useState(false);
  const [coloursOpen, setColoursOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin-lists/nudges");
      setData(data);
    } catch {
      /* Silently ignore — this is a nice-to-have, not critical. */
    }
  };

  const moveToCategory = async (name) => {
    try {
      const { data } = await api.post(
        `/admin-lists/materials/${encodeURIComponent(name)}/move-to-category`,
      );
      toast.success(
        `Moved "${name}" to Category`,
        {
          description: `${data.items_moved} item(s) retagged${
            data.category_created ? ", new category created" : ""
          }.`,
        },
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Could not move "${name}"`);
    }
  };

  useEffect(() => {
    if (isManager) load();
    // Refresh every 60s while the dashboard is open so newly-quick-added
    // entries surface within a minute of creation.
    const id = setInterval(() => isManager && load(), 60_000);
    return () => clearInterval(id);
  }, [isManager]);

  if (!isManager || !data || data.total === 0) return null;

  return (
    <>
      <div
        data-testid="admin-list-nudges-widget"
        className="nb-card p-4 mt-6 border-l-4 border-l-[#5B3A99]"
      >
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={18} strokeWidth={2.5} className="text-[#5B3A99]" />
          <div className="font-display text-lg leading-tight">Tidy up admin lists</div>
          <span className="label-eyebrow px-2 py-0.5 rounded border-2 border-black bg-[#F1E7FA] text-[#5B3A99]">
            {data.total} to enrich
          </span>
        </div>
        <p className="text-xs text-[#607087] mb-3">
          Entries created without a palette, thickness or swatch — or assets/machines missing a department (which hides them from staff). Tap Tidy up to fix — or use <b>→ to Category</b> for items that aren&apos;t really materials (e.g. Pads, Gloves, Sanitiser).
        </p>
        <div className="grid gap-2 md:grid-cols-3">
          <NudgeSection
            icon={PaintBucket}
            title="Materials — no palette"
            rows={data.materials_missing_palette}
            onFix={() => setMaterialsOpen(true)}
            onMove={moveToCategory}
            movable
            testid="nudge-materials-palette"
          />
          <NudgeSection
            icon={Ruler}
            title="Materials — no thickness"
            rows={data.materials_missing_thickness}
            onFix={() => setMaterialsOpen(true)}
            onMove={moveToCategory}
            movable
            testid="nudge-materials-thickness"
          />
          <NudgeSection
            icon={PaintBucket}
            title="Colours — no swatch"
            rows={data.colours_missing_swatch}
            onFix={() => setColoursOpen(true)}
            testid="nudge-colours-swatch"
          />
          <NudgeSection
            icon={Package}
            title="Assets — no department"
            rows={data.assets_missing_dept}
            onFix={() => nav("/assets")}
            testid="nudge-assets-dept"
          />
          <NudgeSection
            icon={Hammer}
            title="Machines — no department"
            rows={data.machines_missing_dept}
            onFix={() => nav("/machinery")}
            testid="nudge-machines-dept"
          />
          <NudgeSection
            icon={DollarSign}
            title="Suppliers — $0 rows backfillable"
            rows={(data.suppliers_zero_cost_movements || []).map((s) => ({
              id: s.id,
              name: `${s.name} · ${s.count}`,
            }))}
            onFix={() => nav("/reports")}
            testid="nudge-suppliers-zero-cost"
          />
        </div>
      </div>

      <ManageMaterialsDialog
        open={materialsOpen}
        onClose={() => {
          setMaterialsOpen(false);
          load();
        }}
      />
      <ManageColoursDialog
        open={coloursOpen}
        onClose={() => {
          setColoursOpen(false);
          load();
        }}
      />
    </>
  );
};

export default AdminListNudgesWidget;
